import { CloseButton, Dialog, DialogBackdrop, DialogPanel, Transition, TransitionChild } from '@headlessui/react';
import { XClose } from '@untitled-ui/icons-react';
import classNames from 'classnames';
import React, { useCallback, useState } from 'react';

import { ModalProps } from '@myhealth-raccoon/components';

import { useMessageListener, usePostMessage } from 'src/contexts/message';

export function ModalLayout({ children }: React.PropsWithChildren) {
    const [open, setOpen] = useState(true);
    const postMessage = usePostMessage();
    useMessageListener(
        useCallback((payload) => {
            if (typeof payload === 'object' && payload.message === 'myhealth-booking-ui-modal') {
                setOpen(payload.value === 'open');
            }
        }, [])
    );

    const handleClose = useCallback(() => {
        setOpen(false);
    }, []);

    const afterClose = useCallback(() => {
        postMessage({ message: 'myhealth-booking-ui-modal', value: 'close' });
    }, [postMessage]);

    return (
        <Modal
            backdropClassname=""
            disableBackdropClickable
            panelClassname=""
            size="xl"
            open={open}
            onClose={handleClose}
            hideX
            unstyled
            afterClose={afterClose}
        >
            {children}
        </Modal>
    );
}

function Modal({
    children,
    open,
    onClose,
    size = 'sm',
    disableBackdropClickable,
    afterClose,
    afterOpen,
    backdropClassname = 'bg-gray-900/30 sm:bg-gray-900/50',
    panelClassname,
    panelStyles,
    panelRef,
}: React.PropsWithChildren<ModalProps>) {
    return (
        <Transition show={open} as={React.Fragment} afterEnter={afterOpen} afterLeave={afterClose}>
            <Dialog className="relative z-10" onClose={onClose}>
                <TransitionChild
                    as={React.Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <DialogBackdrop
                        className={classNames('fixed inset-0 transition-opacity', backdropClassname, {
                            'pointer-events-none': disableBackdropClickable,
                        })}
                    />
                </TransitionChild>
                <div className="fixed inset-0 z-10 flex h-full w-full overflow-hidden sm:px-4">
                    <CloseButton className="absolute right-2 top-2 p-2">
                        <XClose className="size-6 text-white/70" />
                    </CloseButton>
                    <TransitionChild
                        as={React.Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        enterTo="opacity-100 translate-y-0 sm:scale-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                        leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    >
                        <DialogPanel
                            className={classNames(
                                'relative mx-auto mt-16 flex flex-1 flex-col justify-end overflow-hidden text-left drop-shadow-xl transition sm:my-16 sm:justify-center',
                                {
                                    'sm:max-w-sm': size === 'sm',
                                    'sm:max-w-xl': size === 'md',
                                    'sm:max-w-2xl': size === 'lg',
                                    'sm:max-w-4xl': size === 'xl',
                                },
                                panelClassname
                            )}
                            style={panelStyles}
                            ref={panelRef}
                        >
                            {children}
                        </DialogPanel>
                    </TransitionChild>
                </div>
            </Dialog>
        </Transition>
    );
}
